// F-105: make URLs in the specialist's homework text pressable, safely.
// Plain text in, DOM nodes out: the text is never concatenated into HTML. Only http and https links are made; every other scheme (javascript:, data:,
// file:, ftp:, intent:, ...) stays visible text and is never an href. The visible link text is the URL itself (no hidden label), set with textContent,
// and each link sits in a left-to-right isolate so a URL inside Hebrew text keeps its punctuation.
const MAX_SCAN = 20000;                       // longer text is scanned only up to here (the rest stays text); the regex is linear, this just bounds the work
// scheme://, then ASCII URL characters only (so a Hebrew letter right after a URL ends it)
const CAND = /[A-Za-z][A-Za-z0-9+.-]*:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/g;
const TRAIL = '.,;:!?\'"';
const YT_ID = /^[A-Za-z0-9_-]{11}$/;

// Remove the sentence punctuation that follows a URL in prose: . , ; : ! ? quotes, and a closing ) ] that has no opener inside the URL.
function trimTail(c) {
  for (;;) {
    const last = c[c.length - 1];
    if (last && TRAIL.includes(last)) { c = c.slice(0, -1); continue; }
    if (last === ')' && (c.match(/\(/g) || []).length < (c.match(/\)/g) || []).length) { c = c.slice(0, -1); continue; }
    if (last === ']' && (c.match(/\[/g) || []).length < (c.match(/\]/g) || []).length) { c = c.slice(0, -1); continue; }
    return c;
  }
}

// The host must be a registrable-looking name. The check runs on the NORMALISED hostname (u.hostname), not the typed text: new URL() turns every numeric form
// (0, 2130706433, 0x7f.1, 0177.0.0.1, 127.1) into dotted IPv4, which would pass a plain "has a dot" test. So: no IPv4 (normalised), no bracketed IPv6, no empty labels
// ("localhost."), no numeric last label, and nothing under .localhost.
function isNamedHost(h) {
  if (!h || h[0] === '[') return false;                                                  // IPv6 literal
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return false;                                  // IPv4 as normalised by URL (covers 0, decimal, hex, octal, short forms)
  const labels = h.split('.');
  if (labels.length < 2 || labels.some(l => !l)) return false;                           // a dot is needed and no label may be empty (a trailing dot: "localhost.")
  if (!/[A-Za-z]/.test(labels[labels.length - 1])) return false;                         // a name's last label has a letter (a numeric one is an IP form)
  if (labels[labels.length - 1].toLowerCase() === 'localhost') return false;
  return true;
}

// → a safe URL object for an http(s) candidate, else null.
function safeUrl(c) {
  let u; try { u = new URL(c); } catch (_) { return null; }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  if (u.username || u.password) return null;                       // user:pass@host is never made a link
  if (!isNamedHost(u.hostname)) return null;                       // a real, public-looking NAME: never an IP address, never a bare word or localhost
  return u;
}

// text → [{ type: 'text', text } | { type: 'link', text, href }, ...] in order; joining every `text` gives back the input.
export function splitLinks(input) {
  const s = String(input == null ? '' : input);
  const head = s.length > MAX_SCAN ? s.slice(0, MAX_SCAN) : s, rest = s.length > MAX_SCAN ? s.slice(MAX_SCAN) : '';
  const out = []; let last = 0, m;
  CAND.lastIndex = 0;
  while ((m = CAND.exec(head))) {
    const raw = m[0], c = trimTail(raw), u = safeUrl(c);
    if (!u) continue;                                              // not a safe link: it stays part of the surrounding text
    if (m.index > last) out.push({ type: 'text', text: head.slice(last, m.index) });
    out.push({ type: 'link', text: c, href: u.href });
    last = m.index + c.length;
    CAND.lastIndex = last;                                         // the trimmed tail is scanned again as plain text
  }
  if (last < head.length || rest) out.push({ type: 'text', text: head.slice(last) + rest });
  return out;
}

// Append `text` to `parent` as text nodes and anchors. Returns the list of parts (so a caller can look for a YouTube link).
export function linkifyInto(parent, text, doc = document) {
  const parts = splitLinks(text);
  for (const p of parts) {
    if (p.type === 'text') { parent.appendChild(doc.createTextNode(p.text)); continue; }
    const bdi = doc.createElement('bdi'); bdi.setAttribute('dir', 'ltr');
    const a = doc.createElement('a');
    a.href = p.href; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.referrerPolicy = 'no-referrer'; a.className = 'hwp-link';
    a.textContent = p.text;
    bdi.appendChild(a); parent.appendChild(bdi);
  }
  return parts;
}

// YouTube video id of a link, or null. youtube.com/watch?v=, /shorts/, /embed/, /live/, youtu.be/, youtube-nocookie.com/embed/; the id must be exactly 11 safe characters.
export function youtubeId(href) {
  let u; try { u = new URL(href); } catch (_) { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const h = u.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
  let id = null;
  if (h === 'youtu.be') id = u.pathname.split('/')[1] || null;
  else if (h === 'youtube.com' || h === 'youtube-nocookie.com') {
    const seg = u.pathname.split('/').filter(Boolean);
    if (seg[0] === 'watch') id = u.searchParams.get('v');
    else if (['shorts', 'embed', 'live'].includes(seg[0])) id = seg[1] || null;
  }
  return id && YT_ID.test(id) ? id : null;
}

// F-105c / F-105d: an exercise's instructions as runs of plain text and YouTube tiles. EVERY YouTube link (a real 11-character id) becomes a { type:'yt' } run; other links stay
// in the text runs, which the caller linkifies as before. F-105d (planning R-20261021-0017, rules 1-4): a tile takes only the LINK out of its line, and the line's text is
// rejoined as if the link had never been typed.
//   1. The spaces before the link go. A mark from  . , ; : ! ? ) ] ' "  or an ellipsis that follows the link directly stays glued to the word before it; otherwise one space joins the sides.
//   2. A bracket or quote pair left empty  ()  []  ""  ''  is removed.
//   3. A line left with only punctuation, whitespace or a list marker (1.  2)  -  *) is removed, so a link alone on its line still leaves no empty line.
//   4. Text on one line is one run (one paragraph), not one per side of the tile; the line's tiles follow the run. Runs split only where a whole line was the link.
// Language-neutral: no letters or case are touched, and newlines the specialist typed are kept.
export function splitForTiles(input) {
  const parts = splitLinks(input), yts = [];
  let code = 0xE000; while (String(input == null ? '' : input).includes(String.fromCharCode(code))) code++;
  const SENT = String.fromCharCode(code), NL = String.fromCharCode(10), MARKS = ".,;:!?)]'\"" + String.fromCharCode(0x2026);
  let s = '';
  for (const p of parts) {
    const id = p.type === 'link' ? youtubeId(p.href) : null;
    if (id) { yts.push({ type: 'yt', id, href: p.href }); s += SENT; } else s += p.text;
  }
  if (!yts.length) return s.trim() ? [{ type: 'text', text: s }] : [];
  const rejoin = line => {
    for (;;) {
      const i = line.indexOf(SENT); if (i < 0) break;
      const a = line.slice(0, i).replace(/[ \t]+$/, ''), b = line.slice(i + 1);
      if (b && MARKS.includes(b[0])) line = a + b;                                      // rule 1: a mark right after the link stays with the word before
      else line = a && b.trim() ? a + ' ' + b.replace(/^[ \t]+/, '') : a + b.replace(/^[ \t]+/, '');
    }
    for (;;) { const t = line.replace(/\(\s*\)|\[\s*\]|""|''/g, ''); if (t === line) break; line = t; }   // rule 2
    return line.replace(/[ \t]{2,}/g, ' ').trim();
  };
  const dead = t => !/[\p{L}\p{N}]/u.test(t) || /^(\d{1,3}[.)]|[-*])$/.test(t);          // rule 3
  const out = []; let buf = [], queued = [];
  const flush = final => {
    let t = buf.join(NL);
    if (out.length && out[out.length - 1].type === 'yt') t = t.replace(/^(?:[^\S\n]*\n)+/, '');
    if (!final || queued.length) t = t.replace(/(?:\n[^\S\n]*)+$/, '');
    if (t.trim()) out.push({ type: 'text', text: t });
    buf = []; for (const y of queued) out.push(y); queued = [];
  };
  for (const line of s.split(NL)) {
    const k = line.split(SENT).length - 1;
    if (!k) { buf.push(line); continue; }
    const own = yts.splice(0, k), txt = rejoin(line);
    if (dead(txt)) { flush(false); for (const y of own) out.push(y); }                 // the whole line was the link
    else { buf.push(txt); queued.push(...own); }                                      // rule 4: one run per line, its tiles follow it
  }
  flush(true);
  return out;
}

// The first YouTube link among the parts, as { id, href }, or null.
export function firstYoutube(parts) {
  for (const p of parts) if (p.type === 'link') { const id = youtubeId(p.href); if (id) return { id, href: p.href }; }
  return null;
}
